import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { DataTable } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Eye, EyeOff, Chrome, MonitorSmartphone, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import type { ColumnFilterOption } from "@/types";
import { getUserAvatarUrl, getUserInitial } from "@/lib/userProfile";
import { formatGridDate } from "@/lib/utils";

interface UserRecord {
  id: string;
  username: string;
  full_name: string;
  email: string;
  role: string;
  provider?: string;
  created_at: string;
}

interface UserFormData {
  username: string;
  full_name: string;
  email: string;
  role: string;
  password: string;
}

const emptyForm: UserFormData = { username: "", full_name: "", email: "", role: "member", password: "" };

export default function UserManagementPage() {
  const { api, user } = useAuth();
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<UserRecord | null>(null);
  const [form, setForm] = useState<UserFormData>(emptyForm);
  const [showPw, setShowPw] = useState(false);
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});
  const filterTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: 20 };
      const textParts = [columnFilters.full_name, columnFilters.username].filter(Boolean);
      if (textParts.length) params.search = textParts.join(" ");
      if (columnFilters.role) params.role = columnFilters.role;
      const res = await api.get("/auth/users", { params });
      setUsers(res.data.items);
      setTotalPages(res.data.pages);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [api, page, columnFilters]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleColumnFiltersChange = useCallback((filters: Record<string, any>) => {
    setColumnFilters(filters);
    if (filterTimer.current) clearTimeout(filterTimer.current);
    filterTimer.current = setTimeout(() => setPage(1), 400);
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowPw(false);
    setDialogOpen(true);
  };

  const openEdit = (u: UserRecord) => {
    setEditing(u);
    setForm({ username: u.username, full_name: u.full_name, email: u.email || "", role: u.role, password: "" });
    setShowPw(false);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.username) {
      toast.error("Username is required");
      return;
    }
    if (!editing && !form.password) {
      toast.error("Password is required for new user");
      return;
    }
    if (!form.full_name) {
      toast.error("Full name is required");
      return;
    }

    try {
      if (editing) {
        const payload: any = { username: form.username, full_name: form.full_name, email: form.email, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/auth/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/auth/users", form);
        toast.success("User created");
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (id: string) => {
    if (id === user?.id) {
      toast.error("Cannot delete yourself");
      return;
    }
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/auth/users/${id}`);
      toast.success("User deleted");
      fetchUsers();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const roleColor = (role: string) => {
    switch (role) {
      case "admin":
        return "bg-red-100 text-red-800 hover:bg-red-100";
      case "librarian":
        return "bg-blue-100 text-blue-800 hover:bg-blue-100";
      default:
        return "bg-gray-100 text-gray-800 hover:bg-gray-100";
    }
  };

  const roleOptions: ColumnFilterOption[] = [
    { label: "Admin", value: "admin" },
    { label: "Librarian", value: "librarian" },
    { label: "Member", value: "member" },
  ];

  const getProviderMeta = (provider?: string) => {
    if (provider === "google") {
      return { label: "Google", Icon: Chrome, className: "bg-red-100 text-red-800 hover:bg-red-100" };
    }
    if (provider === "microsoft") {
      return { label: "Microsoft", Icon: MonitorSmartphone, className: "bg-blue-100 text-blue-800 hover:bg-blue-100" };
    }
    return { label: "Local", Icon: UserIcon, className: "bg-gray-100 text-gray-800 hover:bg-gray-100" };
  };

  const columns = [
    {
      accessorKey: "full_name",
      header: "Full Name",
      meta: { filterVariant: "text", filterPlaceholder: "Name..." },
      cell: ({ row }: any) => (
        <div className="flex items-center gap-2 min-w-0">
          <Avatar className="h-7 w-7">
            <AvatarImage src={getUserAvatarUrl(row.original, 56)} alt={row.original.full_name || "User"} />
            <AvatarFallback>{getUserInitial(row.original.full_name)}</AvatarFallback>
          </Avatar>
          <span className="font-medium truncate">{row.original.full_name}</span>
        </div>
      ),
    },
    {
      accessorKey: "username",
      header: "Username",
      meta: { filterVariant: "text", filterPlaceholder: "Username..." },
      cell: ({ row }: any) => <span className="font-mono text-xs">{row.original.username}</span>,
    },
    { accessorKey: "email", header: "Email" },
    {
      accessorKey: "provider",
      header: "Provider",
      cell: ({ row }: any) => {
        const meta = getProviderMeta(row.original.provider);
        return (
          <Badge className={meta.className}>
            <meta.Icon className="w-3 h-3 mr-1" />
            {meta.label}
          </Badge>
        );
      },
    },
    {
      accessorKey: "role",
      header: "Role",
      meta: { filterVariant: "select", filterOptions: roleOptions, filterPlaceholder: "Role" },
      cell: ({ row }: any) => <Badge className={roleColor(row.original.role)}>{row.original.role}</Badge>,
    },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ row }: any) => formatGridDate(row.original.created_at),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }: any) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={(e) => {
              e.stopPropagation();
              openEdit(row.original);
            }}
          >
            <Pencil className="w-4 h-4" />
          </Button>
          {row.original.id !== user?.id && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row.original.id);
              }}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            User Management
          </h1>
        </div>
        <Button className="bg-primary hover:bg-primary/90" onClick={openNew} data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add User
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={users}
        totalPages={totalPages}
        currentPage={page}
        onPageChange={setPage}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        loading={loading}
        data-testid="users-table"
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit User" : "Add New User"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Full Name *</Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="user-fullname-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Username *</Label>
              <Input
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                className="font-mono"
                data-testid="user-username-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Role *</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="librarian">Librarian</SelectItem>
                  <SelectItem value="member">Member</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{editing ? "New Password" : "Password *"}</Label>
              <div className="relative">
                <Input
                  type={showPw ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={editing ? "Leave blank to keep" : "Enter password"}
                  className="pr-10"
                  data-testid="user-password-input"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {editing && <p className="text-xs text-muted-foreground">Leave blank to keep current password</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button className="bg-primary hover:bg-primary/90" onClick={handleSave} data-testid="save-user-btn">
              {editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
